import type {
    ChipFormatter,
    CurrencyFormatter,
    DateFormatter,
    LinkFormatter,
    NumberFormatter,
    OptionsFormatter,
    TimeRangeFormatter,
} from '@vnetwork/datacentral-ui/data'
import type { DateRangePreset } from '@vnetwork/datacentral-ui/primitives'
import { paths, generatePathDatacentral, type RouteParams } from '@/utils/path'
import {
    formatMoney as sharedFormatMoney,
    moneyLocale,
    moneyMaxDecimals,
} from '@vnetwork/datacentral-shell/money'
import { DEFAULT_PRESETS, getPresetRange } from '@/utils/date-range-presets'

type Query = Record<string, string | number | (string | number)[] | undefined>

/**
 * Table link cell to an app route: resolves `paths[pathKey]` with params /
 * query from the cell value + row (rendered by `@vnetwork/datacentral-ui` ConfigTable).
 */
export function routeLink<
    T = any,
    K extends keyof typeof paths = keyof typeof paths,
>({
    pathKey,
    pathParams,
    query,
    ...rest
}: Omit<LinkFormatter<T>, 'type' | 'href'> & {
    pathKey: K
    pathParams?: RouteParams<K> | ((value: any, row: T) => RouteParams<K>)
    query?: Query | ((value: any, row: T) => Query)
}): LinkFormatter<T> {
    return {
        type: 'link',
        ...rest,
        href: (value, row) =>
            generatePathDatacentral(
                paths[pathKey],
                (typeof pathParams === 'function'
                    ? pathParams(value, row)
                    : (pathParams ?? {})) as RouteParams<K>,
                typeof query === 'function' ? query(value, row) : query
            ),
    }
}

export const createChipFormatter = <T = any>(
    config: Omit<ChipFormatter<T>, 'type'>
): ChipFormatter<T> => ({ type: 'chip', ...config })

export const createOptionsFormatter = (
    config: Omit<OptionsFormatter, 'type'>
): OptionsFormatter => ({ type: 'options', ...config })

/** `createDateFormatter({ format })`, or the format string alone. */
export const createDateFormatter = (
    config: Omit<DateFormatter, 'type'> | string = {}
): DateFormatter =>
    typeof config === 'string'
        ? { type: 'date', format: config }
        : { type: 'date', ...config }

export const createCurrencyFormatter = (
    config: Omit<CurrencyFormatter, 'type'>
): CurrencyFormatter => ({ type: 'currency', ...config })

/**
 * Money decimals rule: VND → 0 fraction digits; any other currency → up to 4
 * (trailing zeros dropped). Missing currency means VND.
 */
export const currencyMaxDecimals = moneyMaxDecimals

/** `maxDecimals` for a currency/number formatter, read from a row field. */
export const maxDecimalsByCurrency =
    (field = 'service_currency') =>
    (row: any): number =>
        currencyMaxDecimals(row?.[field])

/**
 * Amount + currency symbol, the app's one money format (`20.377.000 ₫` in vi,
 * `₫20,377,000` in en; see `@vnetwork/datacentral-shell/money`) following the
 * same decimals rule — for custom renders next to `currency` formatters.
 */
export const formatMoney = (
    amount: number | string | null | undefined,
    currency?: string | null,
    locale?: string
): string =>
    sharedFormatMoney(amount, {
        currency,
        locale: locale ? moneyLocale(locale) : undefined,
    })

/** The app's standard quick ranges (today, last 7 days, this month…). */
export const DATE_RANGE_PRESETS: DateRangePreset[] = DEFAULT_PRESETS.map(
    (p) => ({
        get label() {
            return p.label
        },
        range: () => {
            const r = getPresetRange(p.name)
            return { from: r.from, to: r.to ?? r.from }
        },
    })
)

export const createTimeRangeFormatter = <T = any>(
    config: Omit<TimeRangeFormatter<T>, 'type'>
): TimeRangeFormatter<T> => ({ type: 'timerange', ...config })

export const createNumberFormatter = (
    config: Omit<NumberFormatter, 'type'> = {}
): NumberFormatter => ({ type: 'number', ...config })

/** Link cell to an absolute / external URL. */
export const createExternalLinkFormatter = <T = any>(
    config: Omit<LinkFormatter<T>, 'type'>
): LinkFormatter<T> => ({ type: 'link', external: true, ...config })
